import React, { useState } from "react";
import { FileSpreadsheet, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useExportOperations } from "@/export/hooks";
import {
  LocationSelection,
  DeviceSelection,
  TimeRangeSelection,
  ExportInfo,
  ReportExportForm,
  ReportJobListPaginated
} from "@/export/components";
import { useReportJobs } from "@/export/api/queries/reports.queries";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/components/ui/tabs";
// import PageHeader from "@/shared/components/layout/dashboard-layout/page-header.tsx";
import { useSetBreadcrumbs } from "@/shared/contexts/breadcrumb-context";
import { useExportBreadcrumbs } from "@/export/hooks/use-export-breadcrumbs.ts";

const ExportPage: React.FC = () => {
  const { t } = useTranslation("export");
  const [activeTab, setActiveTab] = useState("export");
  const navigate = useNavigate();

  const {
    form,
    locations,
    machines,
    locationsLoading,
    machinesLoading,
    handleLocationChange,
    handleDeviceToggle,
    handleSelectAllDevices,
    handleDeselectAllDevices,
    updateForm,
  } = useExportOperations();

  // Fetch export jobs with pagination
  const { data: jobsResponse, isLoading: jobsLoading, error: jobsError } = useReportJobs({
    limit: 50, // Increased limit to show more jobs
    order_by: '-queued_at'
  });

  const crumbs = useExportBreadcrumbs();
  useSetBreadcrumbs(crumbs);

  const handleJobCreated = (_jobId: string) => {
    // Switch to history tab to show the new export job
    setActiveTab("history");
  };

  const handleShowExport = (jobId: string) => {
    navigate(`/export/view/${jobId}`);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-3xl font-bold">{t("page.title")}</h1>
                <p className="text-muted-foreground">{t("page.description")}</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="export" className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                {t("page.tabExport")}
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <History className="h-4 w-4" />
                {t("page.tabHistory")}
              </TabsTrigger>
            </TabsList>

            {/* Export Tab */}
            <TabsContent value="export" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Export Form */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Location Selection */}
                  <LocationSelection
                    selectedLocationId={form.selectedLocationId}
                    locations={locations?.data}
                    isLoading={locationsLoading}
                    onLocationChange={handleLocationChange}
                  />

                  {/* Device Selection */}
                  <DeviceSelection
                    selectedLocationId={form.selectedLocationId}
                    selectedDevices={form.selectedDevices}
                    machines={machines?.data}
                    isLoading={machinesLoading}
                    onDeviceToggle={handleDeviceToggle}
                    onSelectAll={handleSelectAllDevices}
                    onDeselectAll={handleDeselectAllDevices}
                  />

                  {/* Time Range Selection */}
                  <TimeRangeSelection
                    startDate={form.startDate}
                    endDate={form.endDate}
                    startTime={form.startTime}
                    endTime={form.endTime}
                    onStartDateChange={(date) => updateForm({ startDate: date })}
                    onEndDateChange={(date) => updateForm({ endDate: date })}
                    onStartTimeChange={(time) => updateForm({ startTime: time })}
                    onEndTimeChange={(time) => updateForm({ endTime: time })}
                  />
                </div>

                {/* Export Summary & Action */}
                <div className="space-y-6">
                  {/* Background Export Form - Main Export Method */}
                  <ReportExportForm
                    selectedDevices={form.selectedDevices}
                    startDate={form.startDate}
                    endDate={form.endDate}
                    startTime={form.startTime}
                    endTime={form.endTime}
                    selectedLocationId={form.selectedLocationId}
                    onJobCreated={handleJobCreated}
                  />

                  {/* Export Info */}
                  <ExportInfo />
                </div>
              </div>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h2 className="text-2xl font-semibold">{t("page.historyTitle")}</h2>
                  <p className="text-muted-foreground">{t("page.historyDescription")}</p>
                </div>

                <ReportJobListPaginated
                  jobs={jobsResponse?.data || []}
                  isLoading={jobsLoading}
                  error={jobsError}
                  onShow={handleShowExport}
                  itemsPerPage={10}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ExportPage;